import { Application, Container, Graphics } from "pixi.js";
import type { TaskDefinition } from "@talent/protocol";
import {
  SessionRecorder,
  TouchTracker,
  detectImplausibleSpeed,
  detectMultiTouch,
} from "@talent/client-telemetry";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, familyColor, lighten, mix, touchMin } from "./paint.js";

/**
 * Pick-one-of-several, serving three games.
 *
 * **Pattern match** shows a target above and the child finds it below. **Odd one out** shows no
 * target — every shape is the same shape turned except one, and the child finds the odd one.
 * **Letters** shows a letter above and the same letter, sometimes wearing a different shape,
 * among the cards below.
 *
 * One renderer rather than three because the difference between the games is entirely in the
 * DATA: whether a target is present, how the options were generated, and what a card has drawn
 * on it. Three renderers would be three copies of the same layout, hit-testing, feedback and
 * trial-loop code, and they would drift — the layout here already carries four hard-won fixes
 * (recomputing on resize, deriving columns from the touch floor, padded hit areas, centring a
 * short last row) that a copy would have to be given again one at a time.
 *
 * What the letters game needed was none of that. It needed a card with a letter on it instead of
 * a card with dots on it, which is why `paintContent` is a parameter and everything else is not.
 *
 * Replaces `pattern-task.ts`, which drew grey dots on grey squares in silence.
 */

/** How a card is being shown: idle, judged, or the thing to compare against. */
export type CardState = "idle" | "right" | "wrong" | "target";

/**
 * Draws what is ON a card, inside a panel the renderer has already painted.
 *
 * `ink` is the colour the panel decided the content should be — the family accent normally, and
 * the right/wrong colours after a judgement — so a painter never has to know how feedback works.
 * The panel is cleared and its children destroyed before this is called, so a painter may add
 * display objects to `g` without leaking them across a repaint.
 */
export type CardPainter = (
  g: Graphics,
  content: readonly number[],
  size: number,
  state: CardState,
  ink: number,
) => void;

export interface ChoiceTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  /** Correct trials, carried explicitly — a count recovered by rounding a float can be off by
   *  one, and this is the number the child is shown. */
  trialsCorrect: number;
  /** Proportion correct. Raw and task-local; construct estimation is server-side. */
  rawScore: number;
}

/** What both generators produce. `target` is absent for odd-one-out. */
export interface ChoiceStimulus {
  target?: number[];
  options: number[][];
  correctIndex: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateStimulus: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => ChoiceStimulus;
  scoreTrial: (correctIndex: number, chosenIndex: number) => boolean;
  /**
   * What goes on a card. Defaults to the dot grid the two shape games use.
   *
   * Optional rather than required because two of the three games predate it and their cards are
   * this renderer's own idea of a card — making them pass a painter in would be asking two call
   * sites to restate the default.
   */
  paintContent?: CardPainter;
  /** Aborted when the child leaves the game, so the task closes rather than hanging. */
  signal?: AbortSignal;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

export async function runChoiceTask(opts: RunOptions): Promise<ChoiceTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  /**
   * Every sleep inside this task is interruptible.
   *
   * Aliased once rather than threading the signal through a dozen call sites: a task that keeps
   * animating after the child has left is a task whose canvas is still on screen while the next
   * one is being built on top of it.
   */
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  // The play area is shared between games, and it must contain exactly one canvas. The route
  // effect already waits for the previous run to tear down, so this should be a no-op — it is
  // here because two stacked canvases both keep their pointer handlers, which presents as taps
  // going to the wrong game and is very hard to see.
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    const bands = 24;
    for (let i = 0; i < bands; i++) {
      backdrop
        .rect(0, (h / bands) * i, w, h / bands + 1)
        .fill(mix(COLOR.stage, COLOR.stageDeep, i / bands));
    }
    backdrop.circle(w / 2, h * 0.3, Math.min(w, h) * 0.5).fill({ color: accent, alpha: 0.08 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  /**
   * The dot grid: the filled cells as beads, the empty ones as faint wells.
   *
   * Filled dots get the family colour and a highlight; empty cells stay as wells so the grid is
   * legible without drawing lines. The previous version used flat grey on grey, which is most of
   * why the shapes read as diagnostic material rather than as pieces in a game.
   *
   * **The grid dimensions are read HERE rather than at the top of the run.** They are this
   * painter's business and no other caller's: the games whose cards carry a letter, a word or a
   * silhouette have no grid at all, and reading `gridCols` for them produced a `NaN` that sat in
   * scope for the whole sitting waiting for someone to use it.
   */
  const paintDots: CardPainter = (g, filled, size, _state, ink) => {
    const cols = Number(task.params["gridCols"]);
    const rows = Number(task.params["gridRows"]);
    const cellCount = cols * rows;
    const pad = size * 0.14;
    const inner = size - pad * 2;
    const cell = inner / cols;
    const radius = cell * 0.33;
    const set = new Set(filled);

    for (let i = 0; i < cellCount; i++) {
      const cx = -size / 2 + pad + (i % cols) * cell + cell / 2;
      const cy = -size / 2 + pad + Math.floor(i / cols) * cell + cell / 2;
      if (set.has(i)) {
        g.circle(cx, cy, radius).fill(ink);
        // Off-centre highlight: the difference between a dot and a bead.
        g.circle(cx - radius * 0.28, cy - radius * 0.3, radius * 0.34).fill({
          color: 0xffffff,
          alpha: 0.4,
        });
      } else {
        g.circle(cx, cy, radius * 0.42).fill({ color: 0x000000, alpha: 0.16 });
      }
    }
  };
  const paintContent = opts.paintContent ?? paintDots;

  /**
   * Draws one card: the panel, then whatever the game puts on it.
   *
   * **Children are destroyed, not merely removed.** A painter that adds display objects — the
   * letters game adds a `Text` — is called again on every resize, and a card that only cleared
   * its vector drawing would stack a new glyph on the old one each time the address bar slid
   * away. That presents as letters going slowly bold, which is a very odd bug to read.
   */
  function drawCard(
    g: Graphics,
    content: readonly number[],
    size: number,
    state: CardState,
  ): void {
    const panel =
      state === "right"
        ? mix(COLOR.correct, COLOR.stage, 0.55)
        : state === "wrong"
          ? mix(COLOR.wrong, COLOR.stage, 0.55)
          : mix(COLOR.stage, COLOR.card, 0.12);
    const ink =
      state === "right" ? COLOR.correct : state === "wrong" ? COLOR.wrong : accent;

    g.clear();
    for (const child of g.removeChildren()) child.destroy();
    g.roundRect(-size / 2, -size / 2, size, size, size * 0.2).fill(panel);
    // A lit top edge, so the card has volume instead of being a flat rectangle.
    g.roundRect(-size / 2 + size * 0.08, -size / 2 + size * 0.06, size * 0.84, size * 0.16, size * 0.1)
      .fill({ color: 0xffffff, alpha: state === "target" ? 0.16 : 0.07 });

    paintContent(g, content, size, state, ink);
  }

  /**
   * Renders one trial. Resolves with the index the child chose.
   *
   * **Layout is recomputed, not computed once.** On a phone the visual viewport changes size
   * whenever the address bar slides away, which fires a renderer resize — and the first version
   * drew everything from the dimensions at trial start, so after any such change the shapes were
   * still positioned for a screen that no longer existed. Everything positional lives in
   * `layout()` and the resize event calls it.
   *
   * **Columns are derived from the touch minimum rather than the reverse.** The first version
   * computed an option size and then clamped it up to 76 px, which on a narrow screen made four
   * options wider than the screen and pushed two of them off the edge. Now the number of columns
   * is however many fit at the minimum, so the size that comes out already satisfies it.
   *
   * **Four options are always a 2×2 grid, not "however many fit."** At 76 px four boxes are
   * exactly as wide as most phone screens, so `fitting >= count` was true on nearly every real
   * phone and this ended up four boxes crammed edge to edge in a single row on the device the
   * game is actually played on — the opposite of what a grid buys a five-year-old's finger. Four
   * is common enough across the games sharing this renderer that it gets its own rule rather
   * than a width threshold someone has to keep re-tuning against next year's phones.
   */
  function present(stimulus: ChoiceStimulus): {
    chosen: Promise<number>;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);

    const count = stimulus.options.length;
    const hasTarget = stimulus.target !== undefined;

    const target = new Graphics();
    if (hasTarget) layer.addChild(target);

    const shapes = stimulus.options.map(() => {
      const g = new Graphics();
      g.eventMode = "static";
      g.cursor = "pointer";
      layer.addChild(g);
      return g;
    });

    /** Current geometry, refreshed by `layout()` and read by the feedback code. */
    let optionSize = touchMin();
    let states: ("idle" | "right" | "wrong")[] = stimulus.options.map(() => "idle");

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const pad = Math.max(12, w * 0.045);
      const gap = Math.max(10, w * 0.03);
      const usableW = w - pad * 2;

      // The target is capped in absolute terms as well as relative: on a tablet a 44%-of-width
      // shape is enormous and the trial stops reading as a comparison between small things.
      const targetSize = hasTarget ? Math.min(usableW * 0.5, h * 0.26, 210) : 0;
      const targetBlock = hasTarget ? targetSize + gap * 2 : 0;

      const fitting = Math.max(1, Math.floor((usableW + gap) / (touchMin() + gap)));
      // Four is always 2×2, on any screen — see the note on `present()` above for why fitting
      // alone put it in a single row on most phones. Every other count still takes a single row
      // when there is room, and falls back to 2×2-or-wider rather than 3+1 when there is not.
      const columns =
        count === 4 ? 2 : fitting >= count ? count : Math.max(2, Math.min(fitting, count));
      const rows = Math.ceil(count / columns);
      const availableH = h - targetBlock - pad * 2;

      optionSize = Math.min(
        (usableW - gap * (columns - 1)) / columns,
        (availableH - gap * (rows - 1)) / rows,
      );

      const gridH = optionSize * rows + gap * (rows - 1);
      const gridTop = pad + targetBlock + (availableH - gridH) / 2;

      if (hasTarget && stimulus.target) {
        drawCard(target, stimulus.target, targetSize, "target");
        target.position.set(w / 2, pad + targetSize / 2);
      }

      shapes.forEach((g, i) => {
        const col = i % columns;
        const row = Math.floor(i / columns);
        // The last row is centred, so a 2×2 with three items does not sit lopsided.
        const inRow = Math.min(columns, count - row * columns);
        const rowW = optionSize * inRow + gap * (inRow - 1);
        const x = (w - rowW) / 2 + col * (optionSize + gap) + optionSize / 2;
        const y = gridTop + row * (optionSize + gap) + optionSize / 2;
        g.position.set(x, y);
        drawCard(g, stimulus.options[i] ?? [], optionSize, states[i] ?? "idle");
        // Padded hit area: a mis-aimed tap is a motor artefact and must not be scored as a wrong
        // answer. Recomputed with the size, or a resize leaves the hit area behind.
        const half = optionSize / 2 + optionSize * 0.12;
        g.hitArea = {
          contains: (hx: number, hy: number) =>
            hx >= -half && hy >= -half && hx <= half && hy <= half,
        };
      });
    }

    layout();
    app.renderer.on("resize", layout);

    if (hasTarget) {
      glow(glowLayer, target.position.x, target.position.y, optionSize * 0.7, accent, 900);
    }

    let resolveChoice: ((index: number) => void) | null = null;
    let accepting = true;

    shapes.forEach((g, i) => {
      g.on("pointertap", () => {
        if (!accepting || !resolveChoice) return;
        accepting = false;
        // A tone on the tap itself, before anything is evaluated. This was missing: the outcome
        // chimed but the touch was silent, so the two games built on this renderer were the only
        // ones that did not answer a finger. An unacknowledged tap also gets tapped again, which
        // the contamination detectors then read as a multi-touch anomaly.
        cellTone(i * 2, { volume: 0.13 });
        ripple(fx, g.position.x, g.position.y, accent, optionSize);
        resolveChoice(i);
      });

      if (!prefersReducedMotion()) {
        // Staggered entrance. A grid that appears all at once reads as a table; one that arrives
        // in sequence reads as cards being dealt.
        g.scale.set(0);
        void idle(110 + i * 70).then(() => animate(300, (t) => g.scale.set(easeOutBack(t))));
      }
    });

    if (hasTarget && !prefersReducedMotion()) {
      target.scale.set(0);
      void animate(360, (t) => target.scale.set(easeOutBack(t)));
    }

    // A soft tick as the trial opens, so a new question is audible as well as visible. Every other
    // game has an audible beginning; this one started in silence.
    tick();

    const chosen = new Promise<number>((resolve) => {
      resolveChoice = resolve;
    }).then(async (index) => {
      const correct = index === stimulus.correctIndex;
      const picked = shapes[index];

      if (correct) {
        successChime();
        states = states.map((_, i) => (i === index ? "right" : "idle"));
        layout();
        if (picked) {
          burst(fx, picked.position.x, picked.position.y, [
            accent,
            lighten(accent, 0.4),
            COLOR.correct,
          ]);
          await pop(picked, { scale: 1.22 });
        }
        await idle(360);
      } else {
        missTone();
        states = states.map((_, i) => (i === index ? "wrong" : "idle"));
        layout();
        if (picked) await nudge(picked);
        // Show the answer rather than hiding it: the trial should teach, not only score.
        states = states.map((_, i) =>
          i === index ? "wrong" : i === stimulus.correctIndex ? "right" : "idle",
        );
        layout();
        const answer = shapes[stimulus.correctIndex];
        if (answer) {
          glow(glowLayer, answer.position.x, answer.position.y, optionSize * 0.6, COLOR.correct, 700);
          cellTone(4, { volume: 0.09 });
          await pop(answer, { scale: 1.16 });
        }
        await idle(620);
      }
      return index;
    });

    return {
      chosen,
      cleanup: () => {
        accepting = false;
        app.renderer.off("resize", layout);
        layer.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Trial loop
  // ---------------------------------------------------------------------------
  // The level to open at comes from the server's suggestion — the objective the child is working
  // on — and is clamped into this task's range by `newLadder`.
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: ChoiceTaskResult["reason"] = "completed";

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const stimulus = opts.generateStimulus(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // For odd-one-out there is no target, so the first option stands in as the stimulus
        // field the schema requires. `options` and `correctIndex` are what make the trial
        // re-scorable from the record alone, without the client build that produced it.
        stimulus: stimulus.target ?? stimulus.options[0] ?? [],
        options: stimulus.options,
        correctIndex: stimulus.correctIndex,
      });

      const start = performance.now();
      const trial = present(stimulus);
      const outcome = await untilSettled(trial.chosen, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }
      const chosenIndex = outcome;

      const correct = opts.scoreTrial(stimulus.correctIndex, chosenIndex);
      if (correct) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        response: [chosenIndex],
        correct,
        responseMs: elapsed,
      });

      const fast = detectImplausibleSpeed(elapsed, stimulus.options.length);
      if (fast) await recorder.record("input_anomaly", fast);
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      /**
       * The teaching policy lives in `ladder.ts`, not here.
       *
       * A streak raises the level, errors lower it, and the sitting ends when the trials run out —
       * never because a child was struggling (R-5). It was copied into all ten renderers before,
       * which is nine chances for one game to end up quietly stricter than the rest.
       */
      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
    }
  } catch {
    reason = "error";
  } finally {
    const rawScore = trialIndex > 0 ? correctCount / trialIndex : 0;
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
